import { gql } from '@apollo/client';
import { apolloClient } from '../config/apollo-client';

// GraphQL mutation for updating game assignment position
export const UPDATE_GAME_POSITION_MUTATION = gql`
  mutation UpdateAssignmentPosition($input: UpdateLabelAssignmentInput!) {
    updateAssignmentPosition(input: $input) {
      id
      name
      gameId
      comment
      providerId
      categoryId
      launchUrl
      demoAvailable
      freespinAvailable
      deviceType
      betType
      linesCount
      isActive
      iconUrl
      labelIconUrl
      description
      createdAt
      position
    }
  }
`;

// Types for the update assignment position input
export interface UpdateAssignmentPositionInput {
  gameId: number;
  environmentId: number;
  labelId: number;
  position: number;
  comment?: string;
  iconUrl?: string; // per-label icon override; '' clears it
}

// Raw GraphQL response interface (before transformation)
export interface RawGame {
  id: string; // GraphQL ID fields are strings
  name: string;
  gameId: string;
  comment?: string; 
  providerId: number;
  categoryId: number;
  launchUrl?: string | null;
  demoAvailable: boolean;
  freespinAvailable: boolean;
  deviceType: string;
  betType?: string | null;
  linesCount?: number | null;
  isActive: boolean;
  iconUrl?: string | null;
  labelIconUrl?: string | null;
  description?: string | null;
  createdAt: string;
  position: number;
}

// Transformed Game interface (after conversion)
export interface Game {
  id: number;
  name: string;
  gameId: string;
  comment?: string;
  providerId: number;
  categoryId: number;
  launchUrl?: string | null;
  demoAvailable: boolean;
  freespinAvailable: boolean;
  deviceType: string;
  betType?: string | null;
  linesCount?: number | null;
  isActive: boolean;
  iconUrl?: string | null;
  labelIconUrl?: string | null;
  description?: string | null;
  createdAt: string;
  position: number;
}

export interface UpdateGamePositionResponse {
  updateAssignmentPosition: RawGame;
}

// UpdateGamePosition service class
export class UpdateGamePositionService {
  private static instance: UpdateGamePositionService;

  private constructor() { }

  public static getInstance(): UpdateGamePositionService {
    if (!UpdateGamePositionService.instance) {
      UpdateGamePositionService.instance = new UpdateGamePositionService();
    }
    return UpdateGamePositionService.instance;
  }

  // Update game assignment position
  async updateGamePosition(
    input: UpdateAssignmentPositionInput,
  ): Promise<Game> {
    try {
      const { data } = await apolloClient.mutate<UpdateGamePositionResponse>({
        mutation: UPDATE_GAME_POSITION_MUTATION,
        variables: { input },
      });

      if (!data?.updateAssignmentPosition) {
        throw new Error(
          'No data returned from update assignment position mutation',
        );
      }

      // Transform the response to convert string ID to number
      const transformedGame = {
        ...data.updateAssignmentPosition,
        id: Number(data.updateAssignmentPosition.id),
      };

      return transformedGame;
    } catch (error: any) {
      console.error('Error updating game position:', error);
      if (error.graphQLErrors) {
        error.graphQLErrors.forEach((err: any) => {
          console.error('GraphQL Error:', err.message);
          console.error('Locations:', err.locations);
          console.error('Path:', err.path);
        });
      }
      if (error.networkError) {
        console.error('Network Error:', error.networkError);
      }
      throw error;
    }
  }
  // Update game assignment comment
  async updateGameComment(
    input: UpdateAssignmentPositionInput,
  ): Promise<Game> {
    try {
      const { data } = await apolloClient.mutate<UpdateGamePositionResponse>({
        mutation: UPDATE_GAME_POSITION_MUTATION,
        variables: { input },
      });

      if (!data?.updateAssignmentPosition) {
        throw new Error(
          'No data returned from update assignment position mutation',
        );
      }

      // Transform the response to convert string ID to number
      const transformedGame = {
        ...data.updateAssignmentPosition,
        id: Number(data.updateAssignmentPosition.id),
      };

      return transformedGame;
    } catch (error: any) {
      console.error('Error updating game position:', error);
      if (error.graphQLErrors) {
        error.graphQLErrors.forEach((err: any) => {
          console.error('GraphQL Error:', err.message);
          console.error('Locations:', err.locations);
          console.error('Path:', err.path);
        });
      }
      if (error.networkError) {
        console.error('Network Error:', error.networkError);
      }
      throw error;
    }
  }

}

// Export singleton instance
export const updateGamePositionService =
  UpdateGamePositionService.getInstance();

// Export convenience function
export async function updateGamePosition(
  input: UpdateAssignmentPositionInput,
): Promise<Game> {
  return updateGamePositionService.updateGamePosition(input);
}
export async function updateGameComment(
  input: UpdateAssignmentPositionInput,
): Promise<Game> {
  return updateGamePositionService.updateGamePosition(input);
}
